import React, { useState, useEffect } from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Switch,
  Chip,
  Box,
  Typography,
  Tooltip,
  CircularProgress
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import EditIcon from "@material-ui/icons/Edit";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import AddIcon from "@material-ui/icons/Add";
import ForumIcon from "@material-ui/icons/Forum";
import InstagramIcon from "@material-ui/icons/Instagram";
import FacebookIcon from "@material-ui/icons/Facebook";
import { toast } from "react-toastify";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import ConfirmationModal from "../../components/ConfirmationModal";
import MetaCommentAutomationModal from "../../components/MetaCommentAutomationModal";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles
  },
  channelChip: {
    display: "inline-flex",
    alignItems: "center",
    fontWeight: 600,
    fontSize: "0.75rem"
  },
  customTable: {
    minWidth: 700
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(6),
    color: theme.palette.text.secondary
  },
  keywordChip: {
    margin: 2,
    fontSize: "0.7rem",
    height: 22
  }
}));

const MetaCommentAutomations = () => {
  const classes = useStyles();

  const [loading, setLoading] = useState(false);
  const [automations, setAutomations] = useState([]);
  const [selectedChannel, setSelectedChannel] = useState("all");

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedAutomationId, setSelectedAutomationId] = useState(null);

  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [deletingAutomation, setDeletingAutomation] = useState(null);

  const fetchAutomations = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/meta-comments");
      setAutomations(data || []);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAutomations();
  }, []);

  const handleOpenCreate = () => {
    setSelectedAutomationId(null);
    setModalOpen(true);
  };

  const handleOpenEdit = (id) => {
    setSelectedAutomationId(id);
    setModalOpen(true);
  };

  const handleToggleActive = async (automation) => {
    const updatedStatus = !automation.isActive;
    try {
      await api.put(`/meta-comments/${automation.id}`, {
        isActive: updatedStatus
      });
      setAutomations((prev) =>
        prev.map((item) =>
          item.id === automation.id ? { ...item, isActive: updatedStatus } : item
        )
      );
      toast.success(
        updatedStatus
          ? i18n.t("metaComments.messages.activatedSuccess")
          : i18n.t("metaComments.messages.deactivatedSuccess")
      );
    } catch (err) {
      toastError(err);
    }
  };

  const handleDelete = async () => {
    if (!deletingAutomation) return;
    try {
      await api.delete(`/meta-comments/${deletingAutomation.id}`);
      setAutomations((prev) => prev.filter((item) => item.id !== deletingAutomation.id));
      toast.success(i18n.t("metaComments.messages.deletedSuccess"));
    } catch (err) {
      toastError(err);
    } finally {
      setDeletingAutomation(null);
      setConfirmModalOpen(false);
    }
  };

  const filteredAutomations = automations.filter((a) => {
    if (selectedChannel === "all") return true;
    return a.channel === selectedChannel;
  });

  const renderKeywords = (automation) => {
    if (automation.conditionType === "any") {
      return (
        <Chip
          label={i18n.t("metaComments.conditions.any")}
          size="small"
          variant="outlined"
          color="primary"
          style={{ height: 22, fontSize: "0.7rem" }}
        />
      );
    }

    if (!automation.keywords) return "-";

    const kwList = automation.keywords.split(",").map((k) => k.trim()).filter(Boolean);
    return (
      <Box display="flex" flexWrap="wrap" maxWidth={250}>
        {kwList.slice(0, 3).map((kw, i) => (
          <Chip key={i} label={kw} size="small" className={classes.keywordChip} />
        ))}
        {kwList.length > 3 && (
          <Chip label={`+${kwList.length - 3}`} size="small" className={classes.keywordChip} />
        )}
      </Box>
    );
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={i18n.t("metaComments.confirmationModal.deleteTitle")}
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={handleDelete}
      >
        {i18n.t("metaComments.confirmationModal.deleteMessage")}
      </ConfirmationModal>

      <MetaCommentAutomationModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        automationId={selectedAutomationId}
        onSave={fetchAutomations}
      />

      <MainHeader>
        <Title>{i18n.t("metaComments.title")}</Title>
        <MainHeaderButtonsWrapper>
          <Box display="flex" gap={1} mr={2}>
            <Chip
              label={i18n.t("metaComments.filters.all")}
              color={selectedChannel === "all" ? "primary" : "default"}
              onClick={() => setSelectedChannel("all")}
              clickable
            />
            <Chip
              icon={<InstagramIcon style={{ color: "#E1306C" }} />}
              label="Instagram"
              color={selectedChannel === "instagram" ? "primary" : "default"}
              onClick={() => setSelectedChannel("instagram")}
              clickable
            />
            <Chip
              icon={<FacebookIcon style={{ color: "#1877F2" }} />}
              label="Facebook"
              color={selectedChannel === "facebook" ? "primary" : "default"}
              onClick={() => setSelectedChannel("facebook")}
              clickable
            />
          </Box>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleOpenCreate}
          >
            {i18n.t("metaComments.buttons.addRule")}
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined">
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" p={6}>
            <CircularProgress />
          </Box>
        ) : filteredAutomations.length === 0 ? (
          <Box className={classes.emptyState}>
            <ForumIcon style={{ fontSize: 60, opacity: 0.3, marginBottom: 12 }} />
            <Typography variant="h6" gutterBottom>
              {i18n.t("metaComments.empty.title")}
            </Typography>
            <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
              {i18n.t("metaComments.empty.description")}
            </Typography>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleOpenCreate}
            >
              {i18n.t("metaComments.buttons.createFirstRule")}
            </Button>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small" className={classes.customTable}>
              <TableHead>
                <TableRow>
                  <TableCell align="center">{i18n.t("metaComments.table.status")}</TableCell>
                  <TableCell>{i18n.t("metaComments.table.name")}</TableCell>
                  <TableCell>{i18n.t("metaComments.table.channel")}</TableCell>
                  <TableCell>{i18n.t("metaComments.table.connection")}</TableCell>
                  <TableCell>{i18n.t("metaComments.table.scope")}</TableCell>
                  <TableCell>{i18n.t("metaComments.table.trigger")}</TableCell>
                  <TableCell>{i18n.t("metaComments.table.replies")}</TableCell>
                  <TableCell align="center">{i18n.t("metaComments.table.actions")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredAutomations.map((item) => (
                  <TableRow key={item.id} hover>
                    <TableCell align="center">
                      <Tooltip
                        title={
                          item.isActive
                            ? i18n.t("metaComments.status.active")
                            : i18n.t("metaComments.status.inactive")
                        }
                      >
                        <Switch
                          checked={!!item.isActive}
                          onChange={() => handleToggleActive(item)}
                          color="primary"
                          size="small"
                        />
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                        {item.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {item.channel === "instagram" ? (
                        <Chip
                          icon={<InstagramIcon style={{ color: "#E1306C", fontSize: 16 }} />}
                          label="Instagram"
                          size="small"
                          variant="outlined"
                          className={classes.channelChip}
                        />
                      ) : (
                        <Chip
                          icon={<FacebookIcon style={{ color: "#1877F2", fontSize: 16 }} />}
                          label="Facebook"
                          size="small"
                          variant="outlined"
                          className={classes.channelChip}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      {item.whatsapp?.name || `Conexión #${item.whatsappId}`}
                    </TableCell>
                    <TableCell>
                      {item.postId ? (
                        <Tooltip title={`Post ID: ${item.postId}`}>
                          <Chip
                            label={i18n.t("metaComments.scope.specific")}
                            size="small"
                            variant="outlined"
                            style={{ height: 22, fontSize: "0.7rem" }}
                          />
                        </Tooltip>
                      ) : (
                        <Chip
                          label={i18n.t("metaComments.scope.allPosts")}
                          size="small"
                          variant="outlined"
                          style={{ height: 22, fontSize: "0.7rem" }}
                        />
                      )}
                    </TableCell>
                    <TableCell>{renderKeywords(item)}</TableCell>
                    <TableCell>
                      <Box display="flex" gap={0.5} flexDirection="column">
                        {item.publicReplyEnabled && (
                          <Chip
                            label={`💬 Pública (${Array.isArray(item.publicReplies) ? item.publicReplies.length : 1})`}
                            size="small"
                            style={{ height: 20, fontSize: "0.68rem" }}
                          />
                        )}
                        {item.privateReplyEnabled && (
                          <Chip
                            label={`✉️ Privada (DM)${item.flow?.name ? ` ➔ ${item.flow.name}` : ""}`}
                            size="small"
                            color="primary"
                            variant="outlined"
                            style={{ height: 20, fontSize: "0.68rem" }}
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title={i18n.t("common.edit") || "Editar"}>
                        <IconButton size="small" onClick={() => handleOpenEdit(item.id)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={i18n.t("common.delete") || "Eliminar"}>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setDeletingAutomation(item);
                            setConfirmModalOpen(true);
                          }}
                          color="secondary"
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </MainContainer>
  );
};

export default MetaCommentAutomations;
